// @flow
import React, {Suspense, useMemo, useState} from 'react';
import {List, Stack, TextField} from "@mui/material";
import RegistrationReviewService from "../../services/registration-review-service";
import ReviewRequestListItem from "./review-request-list-item";
import {LoadingAvatarListItem} from "../Common/loading-avatar-list-item";
import {withErrorBoundary} from "react-error-boundary";
import Tabbed from '../Common/tabbed';
import {RegistrationRequestController$PendingRequestJson} from "../../api/generated/registry-rest";
import {ErrorBox, InfoBox} from '../Common/alerts';
import {useSuspenseQuery} from "@tanstack/react-query";

const useReviewApplications = () => {
    const {data} = useSuspenseQuery({
        queryKey: ['review-applications'],
        queryFn: async () => RegistrationReviewService.loadReviewRequestsByWarden(),
    });

    return {applications: data};
};

const containsFilterValue = (filterTerm: string) => (application: RegistrationRequestController$PendingRequestJson) => {
    return application.animal.name.toLowerCase().includes(filterTerm)
        || application.breeder.name.toLowerCase().includes(filterTerm)
        || application.rejectionComment.toLowerCase().includes(filterTerm);
};

const ApplicationsList = ({applications}: { applications: RegistrationRequestController$PendingRequestJson[] }) => {
    const [filterTerm, setFilterTerm] = useState('');

    const filteredApplications = useMemo(
        () => applications.filter(containsFilterValue(filterTerm.toLowerCase())),
        [applications, filterTerm]);

    return (
        <Stack padding={2} gap={1}>
            <TextField
                variant="outlined"
                fullWidth
                placeholder="Filter"
                value={filterTerm}
                onChange={ev => setFilterTerm(ev.target.value)}/>
            <List>
                {filteredApplications.map(a => <ReviewRequestListItem key={a.id} application={a}/>)}
            </List>
        </Stack>
    );
};

const ReviewListView = () => {
    const {applications} = useReviewApplications();

    if (!applications.length) {
        return <InfoBox>Keine Anfragen vorhanden</InfoBox>;
    }

    const pending = applications.filter(a => !a.rejectionTime);
    const rejected = applications.filter(a => a.rejectionTime);

    return (
        <Tabbed>
            <Tabbed.Tab label={`Aktiv (${pending.length})`}>
                <ApplicationsList applications={pending} />
            </Tabbed.Tab>
            <Tabbed.Tab label={`Abgelehnt (${rejected.length})`}>
                <ApplicationsList applications={rejected} />
            </Tabbed.Tab>
            <Tabbed.Tab label={`Alle (${applications.length})`}>
                <ApplicationsList applications={applications} />
            </Tabbed.Tab>
        </Tabbed>
    );
};

const LoadingTabsFallback = () => (
    <Tabbed>
        <Tabbed.Tab label="Alle (…)">
            <List><LoadingAvatarListItem/><LoadingAvatarListItem/><LoadingAvatarListItem/></List>
        </Tabbed.Tab>
    </Tabbed>
);

const ReviewList = () => (
    <Suspense fallback={<LoadingTabsFallback/>}>
        <ReviewListView/>
    </Suspense>
);

export default withErrorBoundary(
    ReviewList,
    {
        fallback: <ErrorBox>Fehler beim Rendern der Anfragen!</ErrorBox>
    }
);